<script>
import {mapGetters} from 'vuex';
import SpinnerOverlay from '@/views/common/widgets/SpinnerOverlay';

export default {
   name: 'Flags',
   components: {
      SpinnerOverlay,
   },
   data() {
      return {
         loaded: false,
         hideVerified: false,
         fields: [
            {key: 'issue', class: 'issue-cell'},
            {key: 'status', sortable: true, class: 'no-wrap'},
            {
               key: 'date',
               sortable: true,
               class: 'no-wrap',
               formatter: (val) =>
                  `${new Date(val).toLocaleString(undefined, {month: 'short'})} ${new Date(
                     val
                  ).toLocaleString(undefined, {day: 'numeric'})}, ${new Date(val).toLocaleString(
                     undefined,
                     {year: 'numeric'}
                  )}`,
            },
            {key: 'actions', label: ''},
         ],
      };
   },

   computed: {
      ...mapGetters({
         profile: 'profile',
         isRndig: 'isRndig',
         isClient: 'isClient',
         isCustomer: 'isCustomer',
         flags: 'flags/flags',
         studyPeriodMap: 'companies/studyPeriodMap',
         currentCompany: 'companies/currentCompany',
         activeStudyId: 'companies/activeStudyId',
         isActiveStudyInSetupStage: 'companies/isActiveStudyInSetupStage',
      }),
      companyId() {
         return this.currentCompany.id;
      },
      visibleFlags() {
         let flags = this.flags;

         if (this.hideVerified) {
            flags = flags.filter((flag) => !flag.verified);
         }

         if (this.isRndig || this.isCustomer) {
            return flags;
         }
         return flags.filter((item) => item.userId === this.profile.id);
      },
      hasFlags() {
         return this.visibleFlags.length > 0;
      },
      // The timesurvey module's periods endpoint no longer exists server-side
      // (removed with the legacy timesurvey deletion) -- open study periods are
      // available directly from studyPeriodMap without it.
      openStudyPeriodIds() {
         return Object.values(this.studyPeriodMap)
            .filter((period) => period.study.isOpen)
            .map((period) => period.id);
      },
      flagsTableItems() {
         return this.visibleFlags.map((flag) => {
            return {
               issue: flag.failureInstructions,
               date: flag.createdAt,
               status: flag.severityLevel,
               period: flag.period,
               type: flag.ruleName,
               verified: flag.verified,
               verifiedBy: flag.verifiedBy,
               verifiedByUserId: flag.verifiedByUserId,
            };
         });
      },
   },

   methods: {
      statusVariant(flag) {
         switch (flag.status) {
            case this.$constants().FlagStates.MUST_VERIFY:
               return flag.verified ? 'success' : 'warning';
            case this.$constants().FlagStates.MUST_FIX:
               return 'danger';
         }
         return null;
      },
      statusLabel(flag) {
         switch (flag.status) {
            case this.$constants().FlagStates.MUST_VERIFY:
               return flag.verified ? 'Verified' : 'Must Verify';
            case this.$constants().FlagStates.MUST_FIX:
               return 'Must Fix';
         }
         return 'N/A';
      },
      actionLabel(status) {
         switch (status) {
            case this.$constants().FlagStates.MUST_VERIFY:
               return 'Verify';
            case this.$constants().FlagStates.MUST_FIX:
               return 'Fix';
         }
         return 'N/A';
      },
      async onActionClick(flag) {
         switch (flag.type) {
            case this.$constants().FlagCheckSections.EMPLOYEE_TIME:
               try {
                  const targetPeriodId = this.openStudyPeriodIds.find((periodId) => {
                     const periodBoundaries = this.studyPeriodMap[periodId].period;
                     return (
                        flag.period.lower === periodBoundaries.lower &&
                        flag.period.upper === periodBoundaries.upper
                     );
                  });
                  if (targetPeriodId) {
                     await this.$router.push({
                        name: this.isRndig
                           ? 'employees-period-review-staff'
                           : 'employees-period-review',
                        params: {periodId: targetPeriodId, id: this.companyId},
                     });
                  }
               } catch {
                  // vue-router can throw if navigation is superseded before it resolves.
               }
               break;
            case this.$constants().FlagCheckSections.CONTRACT_RESEARCH:
               try {
                  const targetPeriodId = this.openStudyPeriodIds.find((periodId) => {
                     const periodBoundaries = this.studyPeriodMap[periodId].period;
                     return (
                        flag.period.lower === periodBoundaries.lower &&
                        flag.period.upper === periodBoundaries.upper
                     );
                  });
                  if (targetPeriodId) {
                     await this.$router.push({
                        name: 'contractor-time-data',
                        params: {periodId: targetPeriodId, id: this.companyId},
                     });
                  }
               } catch {
                  // vue-router can throw if navigation is superseded before it resolves.
               }
               break;

            case this.$constants().FlagCheckSections.SUPPLIES:
               try {
                  const targetPeriodId = this.openStudyPeriodIds.find((periodId) => {
                     const periodBoundaries = this.studyPeriodMap[periodId].period;
                     return (
                        flag.period.lower === periodBoundaries.lower &&
                        flag.period.upper === periodBoundaries.upper
                     );
                  });
                  if (targetPeriodId) {
                     await this.$router.push({
                        name: this.isRndig
                           ? 'supplies-period-review-staff'
                           : 'supplies-period-review',
                        params: {periodId: targetPeriodId, id: this.companyId},
                     });
                  }
               } catch {
                  // vue-router can throw if navigation is superseded before it resolves.
               }
               break;

            case this.$constants().FlagCheckSections.CLOUD_COMPUTING:
               try {
                  const targetPeriodId = this.openStudyPeriodIds.find((periodId) => {
                     const periodBoundaries = this.studyPeriodMap[periodId].period;
                     return (
                        flag.period.lower === periodBoundaries.lower &&
                        flag.period.upper === periodBoundaries.upper
                     );
                  });

                  if (targetPeriodId) {
                     await this.$router.push({
                        name: this.isRndig ? 'cloud-period-review-staff' : 'cloud-period-review',
                        params: {periodId: targetPeriodId, id: this.companyId},
                     });
                  }
               } catch {
                  // vue-router can throw if navigation is superseded before it resolves.
               }
               break;
            default:
               return;
         }
      },
      async loadData() {
         this.loaded = false;
         let requests = [
            this.$store.dispatch('flags/loadFlags', {
               companyId: this.companyId,
               studyId: this.activeStudyId,
            }),
         ];

         await this.loadPage(requests);
         this.loaded = true;
      },
   },

   async created() {
      await this.loadData();
   },

   watch: {
      async activeStudyId() {
         if (this.isActiveStudyInSetupStage && !this.isRndig) {
            this.$router.push({name: 'Home'});
            return;
         }
         await this.loadData();
      },
   },
};
</script>

<template>
   <div>
      <div v-if="!loaded">
         <SpinnerOverlay />
      </div>
      <div v-if="loaded">
         <b-container>
            <PageTitle title="Flags and Issues"></PageTitle>
            <ManagerMessage>
               <h2 class="card-title">Issue Tracker</h2>
               <p class="card-description mt-3">
                  This page lists all open issues that require attention. Issues are divided into
                  two categories: Must Fix for critical problems and Must Verify for items pending
                  confirmation. Use this view to track progress and ensure nothing is overlooked.
               </p>
            </ManagerMessage>
         </b-container>

         <b-container>
            <b-card class="flags-card d-relative">
               <div class="d-flex align-items-center justify-content-between" slot="header">
                  <h2 class="card-title mb-1">Open Issues</h2>
                  <b-form-checkbox v-model="hideVerified" class="hide-verified-checkbox">
                     Hide Verified
                  </b-form-checkbox>
               </div>
               <p v-if="hasFlags" class="card-description mt-3 mb-4">
                  {{ isClient ? 'The table below shows all open items. ' : '' }}To resolve an issue,
                  click the <b>Fix</b> or <b>Verify</b> button to go to the relevant page.
               </p>
               <p v-if="!hasFlags" class="card-description mt-3 mb-4">
                  There are currently no flags.
               </p>

               <b-table
                  v-if="hasFlags"
                  :fields="fields"
                  :items="flagsTableItems"
                  sort-by="date"
                  id="table-flags-general"
                  class="mb-3"
               >
                  <template #cell(status)="data">
                     <p :class="`text-${statusVariant(data.item)}`">{{ statusLabel(data.item) }}</p>
                  </template>

                  <template #cell(actions)="data">
                     <div class="cell-w-buttons justify-content-start">
                        <b-button
                           id="btn-flag-action"
                           v-if="!data.item.verified"
                           variant="primary"
                           size="sm"
                           class="button"
                           style="width: 3.75rem"
                           @click="onActionClick(data.item)"
                        >
                           {{ actionLabel(data.item.status) }}
                        </b-button>
                        <p class="verified-by-text" v-if="data.item.verified">
                           Verified by
                           {{
                              data.item.verifiedByUserId === profile.id
                                 ? 'Me'
                                 : data.item.verifiedBy
                           }}
                        </p>
                     </div>
                  </template>
               </b-table>
            </b-card>
         </b-container>
      </div>
   </div>
</template>

<style scoped lang="scss">
.card-title {
   font-size: 1.25rem;
   font-weight: 700;
}

.card-description {
   font-size: 0.8125rem;
}

.flags-card {
   border-radius: 20px;
   box-shadow: 0 4px 20px 0 #005d8b26 !important;
   width: 100%;
}

#table-flags-general {
   font-size: 0.75rem;
}

::v-deep .issue-cell {
   width: 60%;
}

.button {
   font-size: 0.75rem;
}

.verified-by-text {
   margin: 0;
   color: black;
   text-align: left;
   width: 100%;
}

.hide-verified-checkbox {
   font-size: 0.75rem;

   ::v-deep label {
      padding-top: 3px;
   }
}
</style>
